import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { Select, FormControl, InputLabel, MenuItem, Box, CircularProgress } from '@mui/material'

import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const canteenMenuHeaderUserDataFragment = graphql(`
  fragment CanteenLocationSelection_UserDataFragmentV2 on User {
    accountLocation {
      id
    }
    account {
      locationsPaged(filters: { isCCActive: true }) {
        rows {
          id
          label
        }
      }
    }
  }
`)

type Props = {
  userData: FragmentType<typeof canteenMenuHeaderUserDataFragment>
  onChangeAccountLocation: (accountLocationId: number) => void
}

const LocationSelectionCard: React.FC<Props> = ({ userData, onChangeAccountLocation }) => {
  const { account, accountLocation } = useFragment(canteenMenuHeaderUserDataFragment, userData)
  const { t } = useTranslation()

  const locations = account?.locationsPaged.rows || []
  const serverLocation = locations.find((loc) => loc?.id === accountLocation?.id)?.id || ''

  const [optimisticValue, setOptimisticValue] = useState<string>(String(serverLocation))
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    setOptimisticValue(String(serverLocation))
    setIsUpdating(false)
  }, [serverLocation])

  const handleLocationSelection = (value: string) => {
    if (value === String(accountLocation?.id)) return
    setOptimisticValue(value)
    setIsUpdating(true)
    onChangeAccountLocation(Number(value))
  }

  return (
    <Box
      sx={{
        height: '100%',
        paddingTop: optimisticValue ? '12px' : '0px',
      }}>
      <FormControl fullWidth sx={{ height: '100%' }}>
        <InputLabel>{t('cc:navBar.location')}</InputLabel>
        <Select
          value={optimisticValue}
          onChange={(e) => handleLocationSelection(e.target.value)}
          disabled={locations.length <= 1 || isUpdating}
          sx={{
            height: '100%',
            '& .MuiSelect-icon': {
              display: locations.length <= 1 ? 'none' : 'auto',
            },
            '& .MuiSelect-select': {
              paddingY: '8.5px',
            },
          }}
          endAdornment={
            isUpdating ? (
              <CircularProgress size={16} sx={{ position: 'absolute', right: 32 }} />
            ) : undefined
          }>
          {locations.map((location) => (
            <MenuItem key={location?.id} value={location?.id}>
              {location?.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )
}

export default LocationSelectionCard
